<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import {
  BModal, BButton, BAlert, BRow, BCol, BFormGroup, BFormCheckbox, AlertPlugin,
} from 'bootstrap-vue';
import vSelect from 'vue-select';
import { required, email } from '@validations';
import axios from '@/libs/axios';
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue';
import PicFaixasEditor from './PicFaixasEditor.vue';
import { normalizarFaixas, precoPorFaixa } from '@/services/Operacoes/picFaixas';

export default {
  components: {
    PicFaixasEditor,
    BModal,
    BButton,
    BAlert,
    vSelect,
    BRow,
    BCol,
    BFormGroup,
    BFormCheckbox,
    ValidationProvider,
    ValidationObserver,
  },
  props: {
    value: {
      type: Object,
      default: () => ({
        tipopicid: '',
        tipopicdescricao: '',
        tipopicinformavalorbrutoliquido: '',
        tipopictipocontrole: '',
      }),
      require: true,
    },
    tabprecoid: {
      type: String,
      default: null,
      require: false,
    },
    tabelaprecoid: {
      type: [String, Number],
      default: null,
      require: false,
    },
    picsituacao: {
      type: String,
      require: true,
    },
    titleModal: {
      type: String,
      default: null,
      require: false,
    },
    idModal: {
      type: String,
      default: null,
      require: false,
    },
    labelBtnOk: {
      type: String,
      default: null,
      require: false,
    },
  },
  data() {
    return {
      dataFromApiitem: null,
      porFaixa: false,
      faixas: [],
      erro: '',
      isLoading: false,
      precoBaseConsultado: null,
      quoteStatus: 'idle',
      quoteGeneration: 0,
    };
  },
  computed: {
    produtoDescricao() {
      return this.dataForm.DadosItem?.item_descricao || this.dataForm.item_descricao || this.dataForm.itemid || '';
    },
    dataForm: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    isDisabled() {
      return ['Aprovada', 'Pendente'].includes(this.picsituacao);
    },
    totalMercadoria() {
      // Calcula o valor total da mercadoria (quantidade * valor unitário)
      return (this.dataForm.picitemquantidade * this.dataForm.picitemvalorpic).toFixed(2);
    },
    precoBaseAtual() {
      const value = this.precoBaseConsultado != null
        ? this.precoBaseConsultado
        : (this.dataForm.picitemprecobase ?? this.dataForm.picitemvalorpic);
      return Number(value);
    },
    quotePending() {
      return this.quoteStatus === 'pending';
    },
    quoteFailed() {
      return this.quoteStatus === 'failed';
    },
    quoteReady() {
      return this.quoteStatus === 'ready'
        && Number.isFinite(this.precoBaseAtual)
        && this.precoBaseAtual > 0;
    },
    isValorPicDisabled() {
      return true;
    },
    isPorcentagemDisabled() {
      return this.isDisabled;
    },
  },
  watch: {
    totalMercadoria(newValue) {
      this.dataForm.picitemtotalmercadoria = newValue;
    },
  },
  formatCurrency(value) {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  },
  methods: {
    onShow() {
      this.erro = '';
      this.quoteGeneration += 1;
      this.quoteStatus = 'pending';
      this.precoBaseConsultado = null;
      try {
        this.faixas = normalizarFaixas(this.dataForm.picitemfaixas);
        this.porFaixa = this.faixas.length > 0;
      } catch (error) { this.erro = error.message; }
      this.fetchPrecoBase();
    },
    changePricingMode() {
      if (this.porFaixa && !this.faixas.length) this.faixas = [{ min: 1, max: null, desconto: 0 }];
    },
    async validateForm(event) {
      if (event) event.preventDefault();
      if (this.isLoading || this.isDisabled) return;
      if (this.quoteStatus && this.quoteStatus !== 'ready') return;
      this.isLoading = true;
      this.erro = '';
      try {
        const faixas = this.porFaixa ? normalizarFaixas(this.faixas) : [];
        if (this.porFaixa) {
          if (!faixas.length) throw new Error('Adicione pelo menos uma faixa.');
           const quotedBase = this.precoBaseAtualValue
             ? this.precoBaseAtualValue()
             : Number(this.dataForm.picitemprecobase ?? this.dataForm.picitemvalorpic);
           faixas.forEach(row => precoPorFaixa(quotedBase, faixas, row.min));
        }
        if (!Number.isSafeInteger(Number(this.dataForm.picitemquantidade)) || Number(this.dataForm.picitemquantidade) < 1) {
          throw new Error('Informe uma quantidade disponibilizada maior que zero.');
        }
        try {
          const { data } = await axios.get('/pic-item/faixas/capabilities');
          if (data?.quantityTiers !== true || data?.preservesConsumedBalance !== true) throw new Error();
        } catch (_) {
          throw new Error('O servidor ainda não está pronto para salvar faixas. A atualização do backend precisa ser concluída; o item não foi alterado.');
        }
         const precoBase = this.precoBaseAtualValue
           ? this.precoBaseAtualValue()
           : Number(this.dataForm.picitemprecobase ?? this.dataForm.picitemvalorpic);
         const desconto = this.porFaixa ? 0 : Number(this.dataForm.picitemporcentagem || 0);
         const payload = { ...this.dataForm, picitemfaixas: faixas,
           picitemprecobase: precoBase,
           picitemvalorpic: Number((precoBase * (1 - desconto / 100)).toFixed(4)),
           picitemporcentagem: this.porFaixa ? 0 : this.dataForm.picitemporcentagem };
         const tableId = this.tabelaprecoid || this.dataForm.tabelaprecoid || this.dataForm.tabprecoid;
         if (tableId) payload.tabelaprecoid = tableId;
        // The backend preserves consumed units; editing pricing must not refill balance.
        delete payload.picitemquantidadesaldo;
        const response = await axios.post('/pic-item/update', payload);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Sucesso!',
            text: `${response.data.message}`,
            icon: 'CheckIcon',
            variant: 'success',
          },
        });

        this.$bvModal.hide(this.idModal);
        this.$emit('ok');
      } catch (error) {
        this.erro = error.response?.data?.message || error.message || 'Não foi possível salvar o item.';
        console.error('Erro ao chamar a API:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro',
            text: this.erro,
            icon: 'AlertCircleIcon',
            variant: 'error',
          },
        });
      } finally {
        this.isLoading = false;
      }
    },
    toUpperCase() {
      this.dataForm.tipopicid = this.dataForm.tipopicid.toUpperCase();
    },
    async fetchDataFromitem() {
      try {
        const response = await axios.get('/tabela-preco-item/findAllById');
        this.dataFromApiitem = response.data.data.map((item) => ({
          value: item.item && item.item.item_id, // Verifica se item.item existe antes de acessar item_id
          text: item.item && item.item.item_descricao, // Verifica se item.item existe antes de acessar item_descricao
        }));
      } catch (error) {
        console.error('Erro ao chamar a API:', error);
      }
    },
    precoBaseAtualValue() {
      const value = this.precoBaseConsultado != null
        ? this.precoBaseConsultado
        : (this.dataForm.picitemprecobase ?? this.dataForm.picitemvalorpic);
      return Number(value);
    },
    async fetchPrecoBase() {
      const generation = this.quoteGeneration;
      const itemid = this.dataForm.itemid
        ?? this.dataForm.item_id
        ?? this.dataForm.DadosItem?.item_id;
      if (!itemid || !this.dataForm.picid) {
        this.quoteStatus = 'failed';
        this.erro = 'Não foi possível identificar o produto e a PIC para consultar o preço-base.';
        return;
      }
      try {
        const body = { picid: this.dataForm.picid, itemids: [String(itemid)] };
        const tableId = this.tabelaprecoid || this.dataForm.tabelaprecoid || this.dataForm.tabprecoid;
        if (tableId) body.tabelaprecoid = String(tableId);
        const response = await axios.post('/pic-item/precos', body);
        if (generation !== this.quoteGeneration) return;
        const rows = response.data?.data || response.data?.precos || response.data || [];
        const row = rows.find(item => String(item.itemid ?? item.item_id ?? item.itemId) === String(itemid));
        const value = Number(row?.precoBase ?? row?.preco_base ?? row?.preco ?? row?.item_price);
        if (!Number.isFinite(value) || value <= 0) {
          throw new Error(`Preço-base não encontrado para o produto ${itemid}.`);
        }
        this.precoBaseConsultado = value;
        this.quoteStatus = 'ready';
      } catch (error) {
        if (generation !== this.quoteGeneration) return;
        this.precoBaseConsultado = null;
        this.quoteStatus = 'failed';
        this.erro = error.response?.data?.message || error.message
          || 'Não foi possível consultar o preço-base do produto.';
      }
    },
    invalidateQuote() {
      this.quoteGeneration += 1;
      this.quoteStatus = 'idle';
    },
  },
};
</script>

<template>
  <b-modal no-close-on-backdrop no-close-on-esc scrollable size="xl" :id="idModal" :title="titleModal"
    :ok-disabled="isDisabled || isLoading || quotePending || quoteFailed || !quoteReady"
    :cancel-disabled="isLoading" @show="onShow"
    :ok-title="labelBtnOk" ok-variant="success"
    cancel-variant="secondary" body-bg-variant="light" cancel-title="Cancelar" @ok="validateForm"
    @hidden="$emit('cancel'); invalidateQuote()">
    <validation-observer ref="refFormData">
      <b-form @submit.prevent>
        <b-alert :show="!!erro" variant="danger">{{ erro }}</b-alert>
        <b-alert :show="isDisabled" variant="info">Esta PIC está {{ picsituacao }} e não permite alterar os itens. As faixas são configuradas enquanto a PIC está em edição.</b-alert>
        <p>Quantidade disponibilizada é o limite de uso da promoção. Para descontos diferentes conforme a compra, selecione “Por faixa de quantidade”.</p>
        <b-row>
          <b-col cols="4" class="mb-1">
            <b-form-group label="Item" label-for="itemid">
              <validation-provider #default="{ errors }" name="Descrição" rules="required">
                <b-form-input :value="produtoDescricao" id="itemid" readonly />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <!--          <b-col-->
          <!--              cols="4"-->
          <!--              class="mb-1"-->
          <!--          >-->
          <!--            <b-form-group-->
          <!--                label="Sequencia"-->
          <!--                label-for="picitemsequencia"-->
          <!--            >-->
          <!--              <validation-provider-->
          <!--                  #default="{ errors }"-->
          <!--                  name="Descrição"-->
          <!--                  rules="required"-->
          <!--              >-->
          <!--                <b-form-input-->
          <!--                    v-model="dataForm.picitemsequencia"-->
          <!--                    id="picitemsequencia"-->
          <!--                    :state="errors.length > 0 ? false : null"-->
          <!--                    placeholder="Digite a sequencia"-->
          <!--                    :disabled="isDisabled"-->
          <!--                />-->
          <!--                <small class="text-danger">{{ errors[0] }}</small>-->
          <!--              </validation-provider>-->
          <!--            </b-form-group>-->
          <!--          </b-col>-->
          <b-col cols="4" class="mb-1">
            <b-form-group label="Quantidade disponibilizada" label-for="Quantidade">
              <validation-provider #default="{ errors }" name="picitemquantidade" rules="required">
                <b-form-input v-model="dataForm.picitemquantidade" id="picitemquantidade"
                  :state="errors.length > 0 ? false : null" :disabled="isDisabled" />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col cols="4" class="mb-1">
            <b-form-group label="Saldo" label-for="Saldo">
              <validation-provider #default="{ errors }" name="picitemquantidadesaldo" rules="required">
                <b-form-input v-model="dataForm.picitemquantidadesaldo" id="picitemquantidadesaldo"
                  :state="errors.length > 0 ? false : null" disabled />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group label="Como aplicar o desconto?">
              <label class="mr-2"><input v-model="porFaixa" type="radio" :value="false" :disabled="isDisabled" @change="changePricingMode"> Preço ou desconto fixo</label>
              <label><input v-model="porFaixa" type="radio" :value="true" :disabled="isDisabled" @change="changePricingMode"> Por faixa de quantidade</label>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6" class="mb-1">
            <b-form-group :label="porFaixa ? 'Preço-base por unidade (R$), antes do desconto' : 'Preço-base por unidade (R$)'" label-for="picterminovalidpicitemvalorpicade">
              <validation-provider #default="{ errors }" name="picitemvalorpic" rules="required">
                <b-form-input :value="precoBaseAtual" id="picterminovalidpicitemvalorpicade"
                  type="number" min="0.0001" step="0.0001"
                  :state="errors.length > 0 ? false : null" readonly disabled />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col v-if="!porFaixa" cols="4" class="mb-1">
            <b-form-group label="Porcentagem Desconto" label-for="Porcentagem Desconto">
              <validation-provider #default="{ errors }" name="picitemporcentagem" rules="required">
                <b-form-input v-model="dataForm.picitemporcentagem" id="picitemporcentagem"
                  :state="errors.length > 0 ? false : null" :disabled="isPorcentagemDisabled" />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col v-if="!porFaixa" cols="4" class="mb-1">
            <b-form-group label="Total Mercadoria" label-for="Total Mercadoria">
              <validation-provider #default="{ errors }" name="pictotalmercadoria" rules="required">
                <b-form-input v-model="totalMercadoria" id="pictotalmercadoria"
                  :state="errors.length > 0 ? false : null" disabled />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <!--          <b-col-->
          <!--              cols="4"-->
          <!--              class="mb-1"-->
          <!--          >-->
          <!--            <b-form-group-->
          <!--                label="Situação"-->
          <!--                label-for="picitemsituacao"-->
          <!--            >-->
          <!--              <validation-provider-->
          <!--                  #default="{ errors }"-->
          <!--                  name="picitemsituacao"-->
          <!--                  rules="required"-->
          <!--              >-->
          <!--                <b-form-input-->
          <!--                    v-model="dataForm.picitemsituacao"-->
          <!--                    id="picitemsituacao"-->
          <!--                    :state="errors.length > 0 ? false : null"-->
          <!--                    :disabled="isDisabled"-->
          <!--                />-->
          <!--                <small class="text-danger">{{ errors[0] }}</small>-->
          <!--              </validation-provider>-->
          <!--            </b-form-group>-->
          <!--          </b-col>-->
        </b-row>
        <PicFaixasEditor v-if="porFaixa" v-model="faixas" :base="precoBaseAtual" :disabled="isDisabled" />
      </b-form>
    </validation-observer>

  </b-modal>
</template>
